import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { useStudioStore } from '~entities/studios/model/studio-store';

import { QUERY_KEYS } from '~shared/api/query-keys';

import * as api from './email-builder-api';
import type { EmailComposerDefaults } from '../model/types';

export const useComposerDefaults = () => {
  const studioId = useStudioStore((s) => s.currentStudioId);
  return useQuery({
    queryKey: QUERY_KEYS.EMAIL_COMPOSER_DEFAULTS(studioId),
    queryFn: api.getComposerDefaultsAPI,
    enabled: Boolean(studioId),
  });
};

export const useUpdateComposerDefaults = () => {
  const queryClient = useQueryClient();
  const studioId = useStudioStore((s) => s.currentStudioId);
  return useMutation({
    mutationFn: (body: Partial<EmailComposerDefaults>) =>
      api.updateComposerDefaultsAPI(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EMAIL_COMPOSER_DEFAULTS(studioId),
      });
    },
  });
};

export const useCampaignTemplates = (category?: string) => {
  const studioId = useStudioStore((s) => s.currentStudioId);
  return useQuery({
    queryKey: QUERY_KEYS.EMAIL_CAMPAIGN_TEMPLATES(studioId, category),
    queryFn: () => api.listCampaignTemplatesAPI(category),
    enabled: Boolean(studioId),
  });
};

export const useUnlayerTemplates = () => {
  return useQuery({
    queryKey: QUERY_KEYS.UNLAYER_TEMPLATES(),
    queryFn: () =>
      api.listUnlayerTemplatesAPI({ page: 1, perPage: 50, includeDesign: 0 }),
  });
};

export const useUnlayerIdentity = () => {
  return useQuery({
    queryKey: QUERY_KEYS.UNLAYER_IDENTITY(),
    queryFn: api.getUnlayerIdentityAPI,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCampaigns = (status?: string) => {
  const studioId = useStudioStore((s) => s.currentStudioId);
  return useQuery({
    queryKey: QUERY_KEYS.EMAIL_CAMPAIGNS(studioId, status),
    queryFn: () => api.listCampaignsAPI(status),
    enabled: Boolean(studioId),
  });
};

export const useCampaign = (id: string | undefined) => {
  const studioId = useStudioStore((s) => s.currentStudioId);
  return useQuery({
    queryKey: QUERY_KEYS.EMAIL_CAMPAIGN(studioId, id),
    queryFn: () => {
      if (!id) {
        return Promise.reject(new Error('Missing campaign id'));
      }
      return api.getCampaignAPI(id);
    },
    enabled: Boolean(studioId) && Boolean(id),
  });
};

export const useCampaignRecipients = (
  id: string | undefined,
  params?: { page?: number; limit?: number; status?: string },
) => {
  const studioId = useStudioStore((s) => s.currentStudioId);
  return useQuery({
    queryKey: QUERY_KEYS.EMAIL_CAMPAIGN_RECIPIENTS(studioId, id, params),
    queryFn: () => {
      if (!id) {
        return Promise.reject(new Error('Missing campaign id'));
      }
      return api.listCampaignRecipientsAPI(id, params);
    },
    enabled: Boolean(studioId) && Boolean(id),
    placeholderData: keepPreviousData,
  });
};
